<template>
  <div class="d-flex float-left align-items-center">
    <b-button id="btn_print" variant="primary" class="mr-3 wo-footer-btn" @click="pdfgen">
      <i v-if="loading === false" class="bx bx-printer label-icon"></i>
      <spring-spinner class="label-spinner" v-else :animation-duration="3000" :size="20" color="white" />
      View PDF
    </b-button>
    <b-form-checkbox v-model="printDocuments" class="wo-checkbox" tabindex="0">Include documents in pdf</b-form-checkbox>
  </div>
</template>

<script>
import Vue from 'vue';
import { logo } from '../../../assets/images/kaliLogo.js';
import { PDFDocument } from 'pdf-lib';
import VueToast from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import { SpringSpinner } from 'epic-spinners';
import moment from 'moment';

Vue.use(VueToast);

export default {
  name: 'ReportsPDF',
  components: { SpringSpinner },
  props: {
    selectedWorkOrder: Object,
    units: Array,
  },
  data() {
    return {
      printDocuments: false,
      loading: false,
    };
  },

  methods: {
    pdfgen: async function () {
      this.loading = true;
      var pdfMake = require('pdfmake/build/pdfmake.js');
      var pdfFonts = require('pdfmake/build/vfs_fonts.js');
      pdfMake.vfs = pdfFonts.pdfMake.vfs;

      //after creating the pdf report from the document definition, get it in the form of a buffer array.
      const pdfDocGenerator = pdfMake.createPdf(this.buildDocDefinition());
      pdfDocGenerator.getBuffer(async (buffer) => {
        //load the buffer aray into a new PDF document handled by the pdf-lib library, so we can easily append pages to the document.
        const pdfDoc = await PDFDocument.load(buffer);

        //only loop the documents and append if the user has checked the box off ot include documents in the print.
        if (this.printDocuments !== false) {
          //loop through  the reapairs and append the pdfs.
          try {
            for (const repair of this.selectedWorkOrder.repairs) {
              if (repair.documents) {
                //make sure the repair has documents.
                for (const document of repair.documents) {
                  const donorPdfBytes = await fetch(document.url).then((res) =>
                    res.arrayBuffer()
                  );

                  //check to see if we are appending a pdf, jpg, or png
                  if (document.file.type == 'application/pdf') {
                    const donorPdfDoc = await PDFDocument.load(donorPdfBytes);
                    const docLength = donorPdfDoc.getPageCount();
                    for (var k = 0; k < docLength; k++) {
                      const [donorPage] = await pdfDoc.copyPages(donorPdfDoc, [
                        k,
                      ]);
                      pdfDoc.addPage(donorPage);
                    }
                  } else if (
                    document.file.type == 'image/jpeg' ||
                    document.file.type == 'image/jpg' ||
                    document.file.type == 'image/png'
                  ) {
                    let image;
                    //create the pdf image form the buffer array.
                    if (
                      document.file.type == 'image/jpeg' ||
                      document.file.type == 'image/jpg'
                    ) {
                      image = await pdfDoc.embedJpg(donorPdfBytes);
                    } else if (document.file.type == 'image/png') {
                      image = await pdfDoc.embedPng(donorPdfBytes);
                    }
                    //add a new page, so the image is by itself
                    pdfDoc.addPage();

                    //get the page count, so we can append the image to the last page.
                    const pages = pdfDoc.getPages();

                    const jpgDims = image.scaleToFit(
                      pages[pages.length - 1].getWidth(),
                      pages[pages.length - 1].getHeight()
                    ); //set the size of the page, so the image fits on the page.

                    //draw the image to the page.
                    pages[pages.length - 1].drawImage(image, {
                      x:
                        pages[pages.length - 1].getWidth() / 2 -
                        jpgDims.width / 2,
                      y:
                        pages[pages.length - 1].getHeight() / 2 -
                        jpgDims.height / 2,
                      width: jpgDims.width,
                      height: jpgDims.height,
                    });
                  }
                }
              }
            }
          } catch (err) {
            console.log('error', err);
            Vue.$toast.open({
              message: 'An error was encountered generating the work order.',
              type: 'error',
              duration: 4000,
              position: 'top-right',
            });
          }
        }

        let savedPDF = await pdfDoc.save(); //get the modified pdf in a buffer array.

        //load the buffer array into a blob and open it in a new tab so the user can print or download it.
        var pdfFile = new Blob([savedPDF], {
          type: 'application/pdf',
        });
        var pdfUrl = URL.createObjectURL(pdfFile);
        window.open(pdfUrl);
        this.loading = false;
      });
    },
    buildDocDefinition: function () {
      const workOrder = this.selectedWorkOrder;
      return {
        pageMargins: [36, 36, 36, 44],
        footer: (currentPage, pageCount) => ({
          text: [workOrder.number, `Page ${currentPage} of ${pageCount}`].filter(Boolean).join('  |  '),
          style: 'footer',
        }),
        content: [
          {
            columns: [
              { stack: [{ text: 'WORK ORDER', style: 'label' }, { text: workOrder.number || '', style: 'header' }] },
              { image: logo, fit: [110, 40], width: 'auto' },
            ],
          },
          {
            canvas: [{ type: 'line', x1: 0, y1: 0, x2: 523, y2: 0, lineWidth: 1, lineColor: '#d5d9e0' }],
            margin: [0, 8, 0, 12],
          },
          this.buildDetails(),
          { text: `Issues (${workOrder.repairs.length})`, style: 'sectionHeader' },
          ...this.buildIssues(),
        ],
        // keeps an issue's heading on the same page as the start of its repair notes
        pageBreakBefore: (node, followingNodesOnPage) =>
          /^issue\d+$/.test(node.id) &&
          !followingNodesOnPage.some((next) => next.id === `repair${node.id.slice(5)}`),
        defaultStyle: { fontSize: 10, color: '#1f2933' },
        styles: {
          header: { fontSize: 22, bold: true },
          sectionHeader: { fontSize: 12, bold: true, margin: [0, 16, 0, 8] },
          label: { fontSize: 7.5, bold: true, color: '#6b7280', noWrap: true },
          value: { fontSize: 9.5, margin: [0, 1, 0, 0] },
          subValue: { fontSize: 8.5, color: '#6b7280' },
          issueLetter: { fontSize: 10, bold: true, color: 'white', alignment: 'center', noWrap: true },
          issueTitle: { fontSize: 10.5, bold: true, margin: [0, 2, 0, 0] },
          status: { fontSize: 7.5, bold: true, noWrap: true },
          repair: { fontSize: 9.5 },
          noRepair: { fontSize: 9.5, italics: true, color: '#6b7280' },
          footer: { fontSize: 8, color: '#6b7280', alignment: 'right', margin: [36, 16, 36, 0] },
        },
      };
    },
    buildDetails: function () {
      const workOrder = this.selectedWorkOrder;
      const approved = workOrder.repairsApproved.value
        ? `${workOrder.repairsApproved.name} on ${this.pdfDate(workOrder.repairsApproved.time, 'lll')}`
        : '';
      const completedCount = workOrder.repairs.filter((repair) => repair.completed).length;
      return {
        table: {
          widths: ['*', '*', '*', '*'],
          body: [
            [
              this.pdfField('DATE', this.pdfDate(workOrder.date, 'll')),
              this.pdfField('UNIT(S)', this.unitNameString),
              this.pdfField('SUBMITTED BY', workOrder.accepted.name),
              this.pdfField('APPROVED BY', approved),
            ],
            [
              this.pdfField('ODOMETER', workOrder.odometer),
              this.pdfField('HOUR METER', workOrder.hourMeter),
              this.pdfField('PRE-TRIP DEFICIENCY', workOrder.preTripDeficiency ? 'Yes' : 'No'),
              this.pdfField('ISSUES COMPLETED', `${completedCount} of ${workOrder.repairs.length}`),
            ],
          ],
        },
        layout: {
          fillColor: '#f5f6f8',
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: () => 10,
          paddingRight: () => 10,
          paddingTop: (i) => (i === 0 ? 10 : 4),
          paddingBottom: (i, node) => (i === node.table.body.length - 1 ? 10 : 4),
        },
      };
    },
    buildIssues: function () {
      const settings = this.$store.state.custom.workOrders;
      return this.selectedWorkOrder.repairs.map((item, index) => {
        const repairedBy = item.repairedBy
          .filter((repair) => repair.date)
          .map((repair) => ({
            stack: [{ text: repair.name }, { text: this.pdfDate(repair.date, 'll'), style: 'subValue' }],
            margin: [0, 0, 0, 2],
          }));
        const details = [
          this.pdfField('REPAIRED BY', repairedBy),
          this.pdfField('SOURCE OF PARTS', item.sourceOfParts),
        ];
        if (settings.repair_cost_enabled) {
          details.push(this.pdfField('PARTS COST', item.repairCost != null ? `$${item.repairCost}` : ''));
        }
        if (settings.repair_hours_enabled) {
          details.push(this.pdfField('LABOUR COST', item.repairHours != null ? `$${item.repairHours}` : ''));
        }
        if (settings.labour_hours_enabled) {
          details.push(this.pdfField('LABOUR HOURS', item.labourHours));
        }
        details.push(this.pdfField('ODOMETER', item.odometer), this.pdfField('HOUR METER', item.hourMeter));
        const statusColor = item.completed ? '#1e7e34' : '#b54708';
        const repair = (item.repair || '').trim().replace(/\n{3,}/g, '\n\n');

        return {
          id: `issue${index}`,
          margin: [0, 0, 0, 16],
          stack: [
            // marks where the issue starts, so a title bar split off at the bottom of a page still triggers pageBreakBefore
            { text: ' ', fontSize: 1 },
            {
              columnGap: 8,
              columns: [
                {
                  width: 'auto',
                  table: { widths: [16], body: [[{ text: this.issueLetter(index), style: 'issueLetter' }]] },
                  layout: this.pdfTagLayout('#556ee6'),
                },
                { width: '*', text: (item.issue || '').trim(), style: 'issueTitle' },
                {
                  width: 'auto',
                  table: {
                    body: [[{ text: item.completed ? 'COMPLETED' : 'NOT COMPLETED', style: 'status', color: statusColor }]],
                  },
                  layout: this.pdfTagLayout(item.completed ? '#dcefe2' : '#fbe9d6'),
                  margin: [0, 1, 0, 0],
                },
              ],
            },
            {
              canvas: [{ type: 'line', x1: 0, y1: 0, x2: 523, y2: 0, lineWidth: 0.75, lineColor: '#d5d9e0' }],
              margin: [0, 5, 0, 0],
            },
            {
              table: { widths: ['*', '*', ...details.slice(2).map(() => 64)], body: [details] },
              layout: 'noBorders',
              margin: [34, 7, 0, 5],
            },
            { text: 'REPAIR NOTES', style: 'label', margin: [34, 0, 0, 1] },
            {
              id: `repair${index}`,
              text: repair || 'No repair notes.',
              style: repair ? 'repair' : 'noRepair',
              margin: [34, 0, 0, 0],
            },
          ],
        };
      });
    },
    // matches the issue lettering on the work order screen (A–Z, then AA, BB, …)
    issueLetter: function (index) {
      let level = 1;
      while (index >= Math.pow(26, level)) {
        index -= Math.pow(26, level);
        level++;
      }
      return 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[index % 26].repeat(level);
    },
    pdfDate: function (date, format) {
      return moment(date && date.toDate ? date.toDate() : date).format(format);
    },
    // value can be plain text or a list of pdfmake nodes; empty values print as a dash
    pdfField: function (label, value) {
      let content = { text: value === undefined || value === null ? '' : String(value).trim() };
      if (Array.isArray(value)) {
        content = value.length ? { stack: value } : { text: '' };
      }
      if (content.text === '') {
        content.text = '—';
      }
      return { stack: [{ text: label, style: 'label' }, { ...content, style: 'value' }] };
    },
    pdfTagLayout: function (fillColor) {
      return {
        fillColor,
        hLineWidth: () => 0,
        vLineWidth: () => 0,
        paddingLeft: () => 5,
        paddingRight: () => 5,
        paddingTop: () => 2,
        paddingBottom: () => 2,
      };
    },
    getUpToDateUnitName: function (unitUid) {
      let upToDateUnitName = null;
      const unitIndex = this.units.findIndex((x) => x.UID === unitUid);
      if (unitIndex !== -1) {
        upToDateUnitName = this.units[unitIndex].name;
      }
      return upToDateUnitName;
    },
  },
  computed: {
    unitNameString: function () {
      let unitString = '';
      this.selectedWorkOrder.units.forEach((unit, index) => {
        if (index !== this.selectedWorkOrder.units.length - 1) {
          unitString +=
            this.getUpToDateUnitName(unit) !== null
              ? `#${this.getUpToDateUnitName(unit)}, `
              : '';
        } else {
          // last one in array
          unitString +=
            this.getUpToDateUnitName(unit) !== null
              ? `#${this.getUpToDateUnitName(unit)}`
              : '';
        }
      });

      return unitString;
    },
  },
};
</script>
<style scoped>
.label-spinner {
  position: absolute;
  height: 100%;
  left: 10%;
  top: 20%;
}

/* Green outline for blue (primary) buttons */
::v-deep .wo-footer-btn:focus,
::v-deep .wo-footer-btn:focus-visible {
  outline: 2px solid #34c38f !important;
  outline-offset: 2px;
  box-shadow: 0 0 0 3px rgba(52, 195, 143, 0.3) !important;
}

/* Blue outline for the checkbox */
::v-deep .wo-checkbox .custom-control-input:focus ~ .custom-control-label::before {
  border-color: #556ee6 !important;
  box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.3) !important;
}
</style>
